import Reveal from "@/components/Reveal";

/**
 * Manifest — six promises Traeva will put in writing.
 * Variant: "dark" (about page or hero) or "light" (home preview).
 */
const promises = [
  {
    n: "M·01",
    t: "Pricing on the website",
    b: "What you see on this page is what it costs. No discovery-call pricing, no custom quotes that drift upward between the call and the contract.",
  },
  {
    n: "M·02",
    t: "You own everything",
    b: (
      <>
        The account, the listings, the content, the data, the advertising history. If we part
        ways, <b>all of it stays with you</b> — built that way from day one.
      </>
    ),
  },
  {
    n: "M·03",
    t: "90-day exit, no lock-in",
    b: "Partnerships continue because they work, not because a contract traps you. Ninety days' notice, clean handover, no penalty.",
  },
  {
    n: "M·04",
    t: "Success fee, capped",
    b: (
      <>
        10% of the EU revenue we manage — <b>capped at €10,000 a month, in writing.</b> Past the
        cap, every extra euro is entirely yours. Aligned, never punishing.
      </>
    ),
  },
  {
    n: "M·05",
    t: "Founder direct, always",
    b: "No account-manager layer. One operator, reachable, accountable, doing the actual work.",
  },
  {
    n: "M·06",
    t: "Honest verdicts",
    b: (
      <>
        The Bearing says WAIT when launching would waste your money — and tells you what to fix
        first. <b>We'd rather lose the sale than launch a brand that isn't ready.</b>
      </>
    ),
  },
];

export default function Manifest({ variant = "dark" }) {
  const isDark = variant === "dark";
  const sectionCls = isDark ? "bg-ink text-white" : "bg-paper text-ink";
  const borderCls = isDark ? "border-white/15" : "border-line";
  const titleCls = isDark ? "text-white" : "text-ink";
  const ledeCls = isDark ? "text-white/45" : "text-ink-muted";
  const numCls = "text-coral";
  const rowTitleCls = isDark ? "text-white" : "text-ink";
  const rowBodyCls = isDark ? "text-white/55" : "text-ink-muted";
  const rowBoldCls = isDark ? "text-white" : "text-ink";

  return (
    <section className={`${sectionCls} border-b ${borderCls}`} data-testid="manifest-section">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16 sm:py-20 lg:py-24">
        <Reveal>
          <p className={`font-mono text-[10px] tracking-[0.3em] uppercase text-coral flex items-center gap-3.5 mb-5`}>
            <span className="w-9 h-px bg-coral" />
            Chapter 05 — The manifest
          </p>
          <h2 className={`font-serif-i italic text-[34px] sm:text-[44px] lg:text-[64px] leading-[.98] tracking-[-.035em] max-w-[880px] mb-6 ${titleCls}`}>
            Terms most agencies won't
            <br />
            put in <em className="text-coral">writing.</em>
          </h2>
          <p className={`text-[16px] font-light leading-[1.78] max-w-[600px] mb-14 ${ledeCls}`}>
            Six promises. Every one of them is in the contract —{" "}
            <strong className={`${rowBoldCls} font-medium`}>not the sales call.</strong>
          </p>
        </Reveal>

        <div className={`border-t ${borderCls}`}>
          {promises.map((p, i) => (
            <Reveal key={p.n} delay={i * 0.04}>
              <div
                data-testid={`manifest-row-${i}`}
                className={`grid grid-cols-[60px_1fr] sm:grid-cols-[60px_280px_1fr] gap-x-7 gap-y-2 sm:gap-x-8 py-7 border-b ${borderCls} transition-[background-color,padding-left] duration-300 hover:bg-white/[0.025] hover:pl-3`}
              >
                <p className={`font-mono text-[11px] ${numCls} tracking-[0.1em] pt-1`}>{p.n}</p>
                <h3 className={`font-serif-i italic text-[20px] sm:text-[21px] tracking-[-.02em] leading-[1.15] ${rowTitleCls} col-span-1`}>
                  {p.t}
                </h3>
                <p className={`text-[13.5px] font-light leading-[1.72] max-w-[560px] pt-1 sm:pt-[3px] col-span-2 sm:col-span-1 ${rowBodyCls} [&_b]:font-medium [&_b]:${rowBoldCls.replace("text-", "")}`}>
                  <span className={`[&_b]:${rowBoldCls.includes("white") ? "text-white" : "text-ink"}`}>{p.b}</span>
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
